import React, { useEffect, useState, useRef } from 'react';
import styles from './CertificateUpload.module.scss';
import { ReactComponent as PdfIcon } from '../../../icons/pdf-icon-green.svg';
import { ReactComponent as DeleteIcon } from '../../../icons/delete-icon-gray.svg';
import { useDropzone } from 'react-dropzone';
import util from '../../../_helpers/util';
import { confirmAlert } from 'react-confirm-alert';
import { ReactComponent as CloseIcon } from '../../../icons/close-icon.svg';

const UploadedCertificate = ({ certificate, onDeleteUploadedFile }) => {
  return (
    <div className='certificate'>
      <div className='icon-and-name'>
        <PdfIcon />
        <div className='name-and-size'>
          <p>{certificate?.file?.name || certificate.file_name}</p>
          {certificate?.file && (
            <p>{util.fileSize(certificate?.file?.size, true)}</p>
          )}
        </div>
      </div>
      <div
        className='delete-icon'
        onClick={() => onDeleteUploadedFile(certificate)}
      >
        <DeleteIcon className='icon' />
      </div>
    </div>
  );
};

const CertificateUpload = (props) => {
  const {
    onUpload,
    certificate,
    onDeleteUploadedFile,
    uploadAreaId,
    onCloseUploaderArea,
    certificateTypeOptions = [],
    requiredCertificateIds = [],
  } = props;
  const [certType, setCertType] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    multiple: false,
    disabled: !certType || certificate?.type || certificate?.certificate_id,
    accept: ['.pdf'],
  });

  const onFileUpload = () => {
    onUpload(acceptedFiles, certType, certificate?.tempId ?? '');
    setCertType('');
  };

  useEffect(() => {
    if (acceptedFiles.length) {
      if (acceptedFiles[0].type !== 'application/pdf') {
        return confirmAlert({
          title: 'ERROR!',
          message: 'The selected file type is invalid!',
          buttons: [
            {
              label: 'Close',
              onClick: () => {},
            },
          ],
        });
      } else if (acceptedFiles[0].size > MAX_FILE_SIZE) {
        return confirmAlert({
          title: 'ERROR!',
          message:
            'The file size exceeds the maximum limit of 20MB! Please upload a smaller file.',
          buttons: [
            {
              label: 'Close',
              onClick: () => {},
            },
          ],
        });
      }
      onFileUpload();
    }
  }, [acceptedFiles]);

  useEffect(() => {
    setCertType(certificate?.type || certificate?.certificate_id);
  }, [certificate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDisabled =
    certificate?.type || certificate?.certificate_id;
  const selectedOption = certificateTypeOptions.find(
    (opt) => String(opt.id) === String(certType),
  );
  const displayLabel = selectedOption
    ? selectedOption.name
    : 'Select certificate / document type';
  const isRequired = (id) =>
    requiredCertificateIds.some((rid) => String(rid) === String(id));

  return (
    <div>
      <div className={styles.CertificateUpload}>
        <div className='upload-wrapper'>
          <div
            className={`certificate-dropdown certificate-upload-select ${
              isDisabled ? 'cert-disabled' : ''
            }`}
            ref={dropdownRef}
          >
            <button
              type='button'
              className='dropdown-trigger'
              onClick={() => !isDisabled && setDropdownOpen((o) => !o)}
              disabled={isDisabled}
            >
              <span className='dropdown-label'>
                {displayLabel}
                {selectedOption && isRequired(selectedOption.id) && (
                  <span className='required-asterisk'> *</span>
                )}
              </span>
              <span className='dropdown-arrow'>
                {dropdownOpen ? '▲' : '▼'}
              </span>
            </button>
            {dropdownOpen && (
              <ul className='dropdown-options'>
                <li>
                  <button
                    type='button'
                    className='dropdown-option'
                    onClick={() => {
                      setCertType('');
                      setDropdownOpen(false);
                    }}
                  >
                    Select certificate / document type
                  </button>
                </li>
                {certificateTypeOptions.map((option) => (
                  <li key={option.id}>
                    <button
                      type='button'
                      className='dropdown-option'
                      onClick={() => {
                        setCertType(option.id);
                        setDropdownOpen(false);
                      }}
                    >
                      {option.name}
                      {isRequired(option.id) && (
                        <span className='required-asterisk'> *</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div
            {...getRootProps({
              className: `dropzone ${
                (certificate?.type || certificate?.certificate_id) &&
                'cert-disabled'
              }`,
            })}
          >
            <input {...getInputProps()} />
            <p>Choose PDF File</p>
          </div>
          {!certificate && uploadAreaId && (
            <div
              className='close-icon'
              onClick={() => onCloseUploaderArea(uploadAreaId)}
            >
              <CloseIcon />
            </div>
          )}
        </div>
        <div className={`${certificate && 'certificates-list'}`}>
          {certificate && (
            <UploadedCertificate
              certificate={certificate}
              onDeleteUploadedFile={onDeleteUploadedFile}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateUpload;
